<template>
    <div class="student-portal-forum student-portal-inquiries" :class="{ dark }">
        <div class="student-portal-forum__inner">
            <div class="forum-workspace" :class="{ 'is-loading': !hasReady }">
                <div class="forum-workspace__list">
                    <div class="forum-workspace__body">
                        <header class="student-portal-forum__header">
                            <div>
                                <h1 class="student-portal-forum__title">{{ $t('student.inquiry.title') }}</h1>
                                <p class="student-portal-forum__subtitle">{{ $t('student.inquiry.subtitle') }}</p>
                            </div>
                            <v-btn
                                ref="newInquiryButton"
                                color="#16b8c3"
                                variant="flat"
                                prepend-icon="mdi-plus"
                                class="inq-new text-none"
                                @click="openDialogNewInquiry"
                            >
                                {{ $t('student.inquiry.new') }}
                            </v-btn>
                        </header>

                        <div v-if="!hasReady" class="forum-loading">
                            <v-progress-circular indeterminate color="#1ac7d0" size="48" />
                            <p>{{ $t('student.inquiry.loading') }}</p>
                        </div>

                        <template v-else>
                            <section class="forum-stats" :aria-label="$t('student.inquiry.title')">
                                <article class="forum-stat forum-stat--forums">
                                    <span class="forum-stat__icon" aria-hidden="true">
                                        <fa icon="fa-solid fa-comment" />
                                    </span>
                                    <div>
                                        <p class="forum-stat__value">{{ inquiryStats.total }}</p>
                                        <p class="forum-stat__label">{{ $t('student.inquiry.statsTotal') }}</p>
                                        <p class="forum-stat__hint">{{ $t('student.inquiry.statsSent') }}</p>
                                    </div>
                                </article>
                                <article class="forum-stat forum-stat--recent">
                                    <span class="forum-stat__icon" aria-hidden="true">
                                        <fa icon="fa-solid fa-clock" />
                                    </span>
                                    <div>
                                        <p class="forum-stat__value">{{ inquiryStats.waiting }}</p>
                                        <p class="forum-stat__label">{{ $t('student.inquiry.statusWaiting') }}</p>
                                        <p class="forum-stat__hint">{{ $t('student.inquiry.statsWaitingHint') }}</p>
                                    </div>
                                </article>
                                <article class="forum-stat forum-stat--authors">
                                    <span class="forum-stat__icon" aria-hidden="true">
                                        <fa icon="fa-solid fa-circle-check" />
                                    </span>
                                    <div>
                                        <p class="forum-stat__value">{{ inquiryStats.answered }}</p>
                                        <p class="forum-stat__label">{{ $t('student.inquiry.statsAnswered') }}</p>
                                        <p class="forum-stat__hint">{{ $t('student.inquiry.statsAnsweredHint') }}</p>
                                    </div>
                                </article>
                                <article class="forum-stat forum-stat--comments">
                                    <span class="forum-stat__icon" aria-hidden="true">
                                        <fa icon="fa-solid fa-calendar" />
                                    </span>
                                    <div>
                                        <p class="forum-stat__value">{{ inquiryStats.recent }}</p>
                                        <p class="forum-stat__label">{{ $t('student.filters.last7Days') }}</p>
                                        <p class="forum-stat__hint">{{ $t('student.inquiry.statsRecentHint') }}</p>
                                    </div>
                                </article>
                            </section>

                            <div class="forum-filters">
                                <v-text-field
                                    v-model.trim="filters.query"
                                    :placeholder="$t('student.inquiry.searchByTitle')"
                                    :aria-label="$t('student.inquiry.searchByTitle')"
                                    variant="outlined"
                                    density="compact"
                                    clearable
                                    prepend-inner-icon="mdi-magnify"
                                    hide-details
                                />
                                <v-select
                                    v-model="filters.status"
                                    :items="localizedStatusFilterOptions"
                                    item-title="label"
                                    item-value="value"
                                    :label="$t('common.status')"
                                    :aria-label="$t('common.status')"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                />
                                <v-select
                                    v-model="filters.period"
                                    :items="localizedPeriodFilterOptions"
                                    item-title="label"
                                    item-value="value"
                                    :label="$t('student.filters.period')"
                                    :aria-label="$t('student.filters.period')"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                />
                                <v-btn
                                    variant="outlined"
                                    prepend-icon="mdi-filter-off-outline"
                                    class="forum-filters__clear text-none"
                                    :disabled="!hasActiveFilters"
                                    @click="clearFilters"
                                >
                                    {{ $t('student.inquiry.clearFilters') }}
                                </v-btn>
                            </div>

                            <ul v-if="paginatedInquiries.length" class="forum-list">
                                <li v-for="item in paginatedInquiries" :key="item.id">
                                    <article
                                        class="forum-row"
                                        :class="{ 'is-active': selectedInquiry && selectedInquiry.id === item.id }"
                                        tabindex="0"
                                        @click="openDetailsDialog(item)"
                                        @keydown.enter="openDetailsDialog(item)"
                                    >
                                        <span class="forum-row__icon" aria-hidden="true">
                                            <fa icon="fa-solid fa-comment" />
                                        </span>
                                        <div class="forum-row__main">
                                            <h2 class="forum-row__title">{{ item.title }}</h2>
                                            <p v-if="previewText(item.description)" class="forum-row__description">
                                                {{ previewText(item.description) }}
                                            </p>
                                        </div>
                                        <span
                                            class="forum-row__meta"
                                            :class="item.status == 0 ? 'is-waiting' : 'is-answered'"
                                        >
                                            <fa :icon="item.status == 0 ? 'fa-solid fa-clock' : 'fa-solid fa-circle-check'" />
                                            {{ item.status == 0 ? $t('student.inquiry.statusWaiting') : $t('student.inquiry.statusAnswered') }}
                                        </span>
                                        <span class="forum-row__meta">
                                            <fa icon="fa-solid fa-calendar" aria-hidden="true" />
                                            {{ formatListDate(item.dateInsert) }}
                                        </span>
                                        <span v-if="authorName(item)" class="forum-row__author">
                                            <ForumAvatar :name="authorName(item)" size="sm" />
                                            <span class="forum-row__author-name">{{ authorName(item) }}</span>
                                        </span>
                                        <button
                                            type="button"
                                            class="inq-row__delete"
                                            :aria-label="$t('common.delete')"
                                            @click.stop="openDeleteModal(item)"
                                        >
                                            <fa icon="fa-solid fa-trash" />
                                        </button>
                                        <button type="button" class="forum-row__open" @click.stop="openDetailsDialog(item)">
                                            {{ $t('student.inquiry.view') }}
                                        </button>
                                    </article>
                                </li>
                            </ul>

                            <div v-else class="forum-empty">
                                <span class="forum-empty__icon" aria-hidden="true">
                                    <fa icon="fa-solid fa-comment" />
                                </span>
                                <h3>{{ $t('student.inquiry.empty') }}</h3>
                                <p>{{ $t('student.inquiry.emptyHint') }}</p>
                                <div class="forum-empty__action">
                                    <v-btn
                                        color="#16b8c3"
                                        variant="flat"
                                        prepend-icon="mdi-plus"
                                        class="inq-new text-none"
                                        @click="openDialogNewInquiry"
                                    >
                                        {{ $t('student.inquiry.new') }}
                                    </v-btn>
                                </div>
                            </div>
                        </template>
                    </div>

                    <footer v-if="hasReady && filteredData.length" class="forum-pager">
                        <p class="forum-pager__range">{{ showingLabel }}</p>
                        <div class="forum-pager__controls">
                            <label class="forum-pager__size">
                                {{ $t('student.inquiry.perPage') }}
                                <select
                                    :value="itemsPerPage"
                                    :aria-label="$t('student.inquiry.perPage')"
                                    @change="changeItemsPerPage($event.target.value)"
                                >
                                    <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
                                </select>
                            </label>
                            <button
                                type="button"
                                :disabled="currentPage <= 1"
                                :aria-label="$t('student.forum.previousPage')"
                                @click="page = currentPage - 1"
                            >
                                <v-icon size="18">mdi-chevron-left</v-icon>
                            </button>
                            <span>{{ currentPage }}</span>
                            <button
                                type="button"
                                :disabled="currentPage >= pageCount"
                                :aria-label="$t('student.forum.nextPage')"
                                @click="page = currentPage + 1"
                            >
                                <v-icon size="18">mdi-chevron-right</v-icon>
                            </button>
                        </div>
                    </footer>
                </div>

                <aside v-if="hasReady" class="forum-detail">
                    <div v-if="!selectedInquiry" class="forum-empty forum-detail__empty">
                        <span class="forum-empty__icon" aria-hidden="true">
                            <fa icon="fa-solid fa-comment" />
                        </span>
                        <h3>{{ $t('student.inquiry.selectToRead') }}</h3>
                    </div>
                    <template v-else>
                        <div class="forum-detail__back">
                            <button type="button" class="forum-back" @click="clearSelection">
                                <fa icon="fa-solid fa-chevron-left" />
                                {{ $t('common.back') }}
                            </button>
                        </div>
                        <section class="forum-detail__card">
                            <h2 class="forum-detail__title">{{ selectedInquiry.title }}</h2>
                            <p v-if="previewText(selectedInquiry.description)" class="forum-detail__description">
                                {{ previewText(selectedInquiry.description) }}
                            </p>
                            <div class="forum-detail__meta">
                                <span v-if="authorName(selectedInquiry)" class="forum-detail__author">
                                    <ForumAvatar :name="authorName(selectedInquiry)" size="sm" />
                                    {{ authorName(selectedInquiry) }}
                                </span>
                                <span class="forum-detail__meta-item">
                                    <fa icon="fa-solid fa-calendar" />
                                    {{ formatDetailDate(selectedInquiry.dateInsert) }}
                                </span>
                                <span
                                    class="forum-detail__meta-item"
                                    :class="selectedInquiry.status == 0 ? 'is-waiting' : 'is-answered'"
                                >
                                    <fa :icon="selectedInquiry.status == 0 ? 'fa-solid fa-clock' : 'fa-solid fa-circle-check'" />
                                    {{ selectedInquiry.status == 0 ? $t('student.inquiry.statusWaiting') : $t('student.inquiry.statusAnswered') }}
                                </span>
                            </div>
                        </section>
                        <div class="forum-detail__scroll">
                            <ul class="forum-comments">
                                <li v-if="selectedInquiry.description">
                                    <ForumCommentCard
                                        :comment="{
                                            id: 'origin-' + selectedInquiry.id,
                                            userName: authorName(selectedInquiry),
                                            dateInsert: selectedInquiry.dateInsert,
                                            comment: selectedInquiry.description,
                                        }"
                                    />
                                </li>
                                <li v-for="message in selectedMessages" :key="message.id">
                                    <ForumCommentCard
                                        :comment="{
                                            id: message.id,
                                            userName: message.userName,
                                            dateInsert: message.dateInsert,
                                            comment: message.message,
                                        }"
                                    />
                                </li>
                            </ul>
                            <p v-if="!selectedInquiry.description && !selectedMessages.length" class="forum-comments-empty">
                                {{ $t('student.inquiry.noReplies') }}
                            </p>
                        </div>
                        <form class="forum-editor" @submit.prevent="newComment(selectedInquiry.id, selectedInquiryIndex)">
                            <p v-show="selectedInquiry.status == 0" class="forum-comments-empty">
                                {{ $t('student.inquiry.waitingTeam') }}
                            </p>
                            <label class="forum-editor__title" :for="'inquiry_reply_' + selectedInquiry.id">
                                {{ $t('student.inquiry.replyTitle') }}
                            </label>
                            <textarea
                                :id="'inquiry_reply_' + selectedInquiry.id"
                                v-model="comment[selectedInquiryIndex]"
                                class="forum-editor__input"
                                rows="3"
                                :placeholder="$t('student.inquiry.writeMessage')"
                            ></textarea>
                            <div class="forum-editor__actions">
                                <button type="submit" class="forum-send" :disabled="!comment[selectedInquiryIndex]">
                                    <fa icon="fa-solid fa-paper-plane" />
                                    {{ $t('student.inquiry.sendReply') }}
                                </button>
                            </div>
                        </form>
                    </template>
                </aside>
            </div>
        </div>
        <v-dialog
            v-model="newInquiryDialog"
            max-width="760"
            class="new-inquiry-dialog"
            aria-labelledby="new-inquiry-title"
        >
            <div class="new-question-modal">
                <div class="new-question-modal__content">
                    <header class="new-question-modal__header">
                        <h2 id="new-inquiry-title" class="new-question-modal__title">
                            {{ $t('student.inquiry.new') }}
                        </h2>
                        <p class="new-question-modal__subtitle">
                            {{ $t('student.inquiry.modalSubtitle') }}
                        </p>
                        <button
                            type="button"
                            class="new-question-modal__close"
                            :aria-label="$t('student.inquiry.closeModal')"
                            @click="newInquiryDialog = false"
                        >
                            <fa icon="fa-solid fa-xmark" />
                        </button>
                    </header>

                    <form class="new-question-form" @submit.prevent>
                        <div class="new-question-field">
                            <label class="new-question-field__label" for="new-inquiry-title-input">
                                {{ $t('student.inquiry.titleField') }}
                                <span class="new-question-field__required" aria-hidden="true">*</span>
                            </label>
                            <div class="input-with-icon">
                                <span class="input-with-icon__icon" aria-hidden="true">
                                    <fa icon="fa-solid fa-comment" />
                                </span>
                                <input
                                    id="new-inquiry-title-input"
                                    ref="newInquiryTitle"
                                    v-model="newInquiry.title"
                                    class="question-title-input"
                                    type="text"
                                    required
                                    :placeholder="$t('student.inquiry.titlePlaceholder')"
                                />
                            </div>
                        </div>

                        <div class="new-question-field">
                            <label class="new-question-field__label" for="new-inquiry-description-input">
                                {{ $t('student.inquiry.descriptionField') }}
                                <span class="new-question-field__required" aria-hidden="true">*</span>
                            </label>
                            <div class="textarea-with-icon">
                                <span class="textarea-with-icon__icon" aria-hidden="true">
                                    <fa icon="fa-solid fa-file-lines" />
                                </span>
                                <textarea
                                    id="new-inquiry-description-input"
                                    v-model="newInquiry.description"
                                    class="question-description-textarea"
                                    :placeholder="$t('student.inquiry.descriptionPlaceholder')"
                                ></textarea>
                            </div>
                        </div>

                        <div class="new-question-modal__footer">
                            <button
                                type="button"
                                class="cancel-question-button"
                                @click="newInquiryDialog = false"
                            >
                                {{ $t('common.cancel') }}
                            </button>
                            <button
                                type="button"
                                class="submit-question-button"
                                :disabled="!newInquiry.title"
                                @click="createNewInquiryStudent"
                            >
                                <fa icon="fa-solid fa-paper-plane" />
                                {{ $t('student.inquiry.send') }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </v-dialog>
        <v-dialog
            v-model="deletInquiryDialog"
            max-width="500"
            class="delete-inquiry-dialog"
        >
            <v-card class="dialog-card delete-inquiry-dialog__card">
                <div class="modal-header mb-3">
                    <h5 class="modal-title">
                        {{ $t('student.inquiry.deleteTitle') }}
                    </h5>
                </div>
                <v-card-text class="dialog-content p-0">
                    <p>{{ $t('student.inquiry.deleteConfirm') }}</p>
                </v-card-text>
                <v-card-actions class="dialog-actions px-3">
                    <v-btn
                        class="dialog-btn cancel"
                        text
                        @click="deletInquiryDialog = false"
                        >{{ $t('common.cancel') }}</v-btn
                    >
                    <v-btn
                        class="dialog-btn confirm"
                        text
                        @click="deletInquiry()"
                        >{{ $t('common.delete') }}</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>

    </div>
</template>

<script>
    import api from '@/utils/portal/inquiry.js';
    import ForumAvatar from '@/components/Student/PortalForum/ForumAvatar.vue';
    import ForumCommentCard from '@/components/Student/PortalForum/ForumCommentCard.vue';
    import { mapState } from 'vuex';
    import { toast } from 'vue3-toastify';


    export default {
        data() {
            return {
                active: 0,
                data: [],
                hasReady: false,
                comment: [],
                activeInquiries: {},
                newInquiryDialog: false,
                newInquiry: {
                    title: '',
                    description: '',
                },
                deletInquiryDialog: false,
                deletedInquiry: {
                    id: '',
                    index: '',
                },
                detailsDialog: false,
                selectedInquiry: null,
                selectedInquiryIndex: null,
                selectionCleared: false,
                authorById: {},
                page: 1,
                itemsPerPage: 10,
                pageSizeOptions: [10, 25, 50, 100],
                filters: {
                    query: '',
                    status: 'all',
                    period: 'all',
                },
            };
        },
        props: {},
        computed: {
            ...mapState(['token']),
            ...mapState(['dark']),
            localizedStatusFilterOptions() {
                return [
                    { label: this.$t('common.all'), value: 'all' },
                    { label: this.$t('student.inquiry.statusWaiting'), value: 0 },
                    { label: this.$t('student.inquiry.statusAnswered'), value: 1 },
                ];
            },
            localizedPeriodFilterOptions() {
                return [
                    { label: this.$t('student.filters.allPeriod'), value: 'all' },
                    { label: this.$t('student.filters.last7Days'), value: 7 },
                    { label: this.$t('student.filters.last30Days'), value: 30 },
                    { label: this.$t('student.filters.last90Days'), value: 90 },
                ];
            },
            localizedHeaders() {
                return [
                    {
                        title: this.$t('common.title'),
                        key: 'title',
                        sortable: true,
                        width: '40%',
                        minWidth: 200,
                        align: 'start',
                    },
                    {
                        title: this.$t('student.inquiry.tableDateSent'),
                        key: 'dateInsert',
                        sortable: true,
                        width: '20%',
                        align: 'center',
                        class: 'd-none d-md-table-cell',
                    },
                    {
                        title: this.$t('common.status'),
                        key: 'status',
                        sortable: true,
                        width: '15%',
                        align: 'center',
                    },
                    {
                        title: this.$t('common.actions'),
                        key: 'actions',
                        sortable: false,
                        width: '25%',
                        align: 'center',
                    },
                ];
            },
            shouldHidePagination() {
                return this.filteredData.length <= this.itemsPerPage;
            },
            hasActiveFilters() {
                return (
                    String(this.filters.query || '').trim() !== '' ||
                    this.filters.status !== 'all' ||
                    this.filters.period !== 'all'
                );
            },
            inquiryStats() {
                const total = this.data.length;
                const waiting = this.data.filter((item) => item.status == 0).length;
                const now = Date.now();
                const recent = this.data.filter((item) => {
                    if (!item.dateInsert) return false;
                    const date = new Date(item.dateInsert);
                    if (Number.isNaN(date.getTime())) return false;
                    return (now - date.getTime()) / (1000 * 60 * 60 * 24) <= 7;
                }).length;
                return {
                    total,
                    waiting,
                    answered: total - waiting,
                    recent,
                };
            },
            pageCount() {
                return Math.max(1, Math.ceil(this.filteredData.length / this.itemsPerPage));
            },
            currentPage() {
                return Math.min(this.page, this.pageCount);
            },
            paginatedInquiries() {
                const start = (this.currentPage - 1) * this.itemsPerPage;
                return this.filteredData.slice(start, start + this.itemsPerPage);
            },
            showingLabel() {
                const total = this.filteredData.length;
                if (!total) return '';
                const from = (this.currentPage - 1) * this.itemsPerPage + 1;
                const to = Math.min(this.currentPage * this.itemsPerPage, total);
                const key = total === 1 ? 'student.inquiry.showingOne' : 'student.inquiry.showingMany';
                return this.$t(key, { from, to, total });
            },
            selectedMessages() {
                const messages = this.selectedInquiry && this.selectedInquiry.messages;
                if (!Array.isArray(messages)) return [];
                return [...messages].reverse();
            },
            filteredData() {
                const q = String(this.filters.query || '').toLowerCase();
                const status = this.filters.status;
                const period = this.filters.period;
                const now = new Date();

                return this.data.filter((item) => {
                    const byQuery =
                        !q ||
                        String(item.title || '')
                            .toLowerCase()
                            .includes(q);

                    const byStatus = status === 'all' || item.status === status;

                    let byPeriod = true;
                    if (period !== 'all' && item.dateInsert) {
                        const dt = new Date(item.dateInsert);
                        if (Number.isNaN(dt.getTime())) {
                            byPeriod = false;
                        } else {
                            const diffMs = now.getTime() - dt.getTime();
                            const diffDays = diffMs / (1000 * 60 * 60 * 24);
                            byPeriod = diffDays <= Number(period);
                        }
                    }

                    return byQuery && byStatus && byPeriod;
                });
            },
        },
        components: {
            ForumAvatar,
            ForumCommentCard,
        },
        methods: {
            handleOpenModalInquiry() {
                this.openDialogNewInquiry = false;
            },
            openDetailsDialog(item) {
                this.selectionCleared = false;
                this.selectedInquiry = item;
                const index = this.data.findIndex((entry) => entry.id === item.id);
                this.selectedInquiryIndex = index === -1 ? 0 : index;
            },
            clearSelection() {
                this.selectionCleared = true;
                this.selectedInquiry = null;
                this.selectedInquiryIndex = null;
            },
            ensureSelection() {
                const visible = this.filteredData;
                if (this.selectedInquiry) {
                    const current = visible.find((item) => item.id === this.selectedInquiry.id);
                    if (current) {
                        this.selectedInquiry = current;
                        const index = this.data.findIndex((entry) => entry.id === current.id);
                        this.selectedInquiryIndex = index === -1 ? 0 : index;
                        return;
                    }
                }
                if (this.selectionCleared || !visible.length) {
                    this.selectedInquiry = null;
                    this.selectedInquiryIndex = null;
                    return;
                }
                this.openDetailsDialog(visible[0]);
            },
            changeItemsPerPage(value) {
                this.itemsPerPage = Number(value) || 10;
                this.page = 1;
            },
            previewText(html) {
                return String(html || '')
                    .replace(/<[^>]*>/g, ' ')
                    .replace(/&nbsp;/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim();
            },
            formatListDate(value) {
                const date = new Date(value);
                if (Number.isNaN(date.getTime())) return '';
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                return `${day}/${month}/${date.getFullYear()}`;
            },
            formatDetailDate(value) {
                const date = new Date(value);
                if (Number.isNaN(date.getTime())) return '';
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                return `${this.formatListDate(value)}, ${hours}:${minutes}`;
            },
            initials(name) {
                const parts = String(name || '')
                    .trim()
                    .split(' ')
                    .filter((word) => word.length > 0)
                    .slice(0, 2);
                if (!parts.length) return '??';
                return parts.map((word) => word[0].toUpperCase()).join('');
            },
            authorName(item) {
                if (!item) return '';
                return this.authorById[item.id] || '';
            },
            isTeamMessage(message) {
                const author = String(this.authorName(this.selectedInquiry) || '').trim().toLowerCase();
                const name = String((message && message.userName) || '').trim().toLowerCase();
                return Boolean(author && name && name !== author);
            },
            toggleInquiry(index) {
                this.activeInquiries[index] = !this.activeInquiries[index];
            },
            openDialogNewInquiry() {
                this.newInquiryDialog = true;
            },
            async createNewInquiryStudent() {
                const payload = {
                    title: this.newInquiry.title,
                    description: this.newInquiry.description,
                    status: 1,
                };
                try {
                    const response = await api.newInquiry(this.token, payload);
                    this.getDataInquirys();
                    toast.success(this.$t('student.inquiry.toastCreated'), { className: 'inq-toast' })
                    this.newInquiryDialog = false;
                    this.newInquiry = { title: '', description: '' };
                } catch (error) {
                    toast.error(this.$t('student.inquiry.toastCreateError'));
                    console.error('Erro ao criar dúvida:', error);
                }
            },
            async getDataInquirys(silent = false) {
                if (!silent) this.hasReady = false;
                try {
                    const response = await api.getDataListInquiry(this.token);
                    this.data = response;
                    this.activeInquiries[0] = true;
                    this.transformFirstMessagesInDescription();
                    this.hasReady = true;
                    this.ensureSelection();
                } catch (error) {
                    toast.error(this.$t('student.inquiry.toastLoadError'));
                    console.error('Erro na listagem da notificações');
                }
            },
            formatarData(data) {
                const dataObj = new Date(data);
                const dia = dataObj.getDate();
                const mes = dataObj.getMonth() + 1;
                const ano = dataObj.getFullYear() % 100;

                const diaFormatado = dia < 10 ? '0' + dia : dia;
                const mesFormatado = mes < 10 ? '0' + mes : mes;
                const anoFormatado = ano < 10 ? '0' + ano : ano;

                return `${diaFormatado}/${mesFormatado}/${anoFormatado}`;
            },
            clearFilters() {
                this.filters.query = '';
                this.filters.status = 'all';
                this.filters.period = 'all';
            },
            async newComment(id, index) {
                const obj = {
                    message: this.comment[index],
                };
                try {
                    await api.newComment(this.token, obj, id);
                    this.comment[index] = '';
                    await this.getDataInquirys(true);
                    const current = this.data.find((item) => item.id === id);
                    if (current) {
                        this.openDetailsDialog(current);
                        this.comment[this.selectedInquiryIndex] = '';
                    }
                } catch (error) {
                    toast.error(this.$t('student.inquiry.toastCommentError'));
                    console.error('Erro ao enviar comentário da dúvida');
                }
            },
            transformFirstMessagesInDescription() {
                if (this.data.length > 0) {
                    for (let i = 0; i < this.data.length; i++) {
                        if (
                            this.data[i].messages &&
                            this.data[i].messages.length > 0
                        ) {
                            const firstMessage = this.data[i].messages.pop(
                                this.data[i].messages.length - 1
                            );
                            this.data[i].description = firstMessage.message;
                            if (firstMessage && firstMessage.userName) {
                                this.authorById[this.data[i].id] = firstMessage.userName;
                            }
                        } else {
                            this.data[i].description = '';
                        }
                    }
                }
            },
            openDeleteModal(item, index) {
                this.deletedInquiry.id = item.id;
                const dataIndex = this.data.findIndex((entry) => entry.id === item.id);
                this.deletedInquiry.index = dataIndex === -1 ? index : dataIndex;
                this.deletInquiryDialog = true;
            },
            deletInquiry() {
                const deletedInquiry = this.data.find(
                    (inquiry) => inquiry.id === this.deletedInquiry.id
                );
                const payload = {
                    ...deletedInquiry,
                    status: 9,
                };
                api
                    .updateInquiry(this.token, payload, this.deletedInquiry.id)
                    .then(() => {
                        const removedId = this.deletedInquiry.id;
                        const index = this.data.findIndex((entry) => entry.id === removedId);
                        if (index !== -1) this.data.splice(index, 1);
                        if (this.selectedInquiry && this.selectedInquiry.id === removedId) {
                            this.clearSelection();
                        }
                        this.deletInquiryDialog = false;
                        this.deletedInquiry = { id: '', index: '' };
                    })
                    .catch((error) => {
                        console.error('Erro ao deletar a dúvida:', error);
                    });
            }
        },
        created() {
            this.getDataInquirys();
        },
        watch: {
            filters: {
                deep: true,
                handler() {
                    this.page = 1;
                },
            },
            filteredData() {
                if (this.page > this.pageCount) this.page = this.pageCount;
                this.ensureSelection();
            },
            openDialogNewInquiry(novoValor) {
                if (novoValor == false) {
                    setTimeout(() => {
                        this.getDataInquirys();
                    }, 1000);
                }
                document.body.style.overflow = novoValor ? 'hidden' : '';
            },
            newInquiryDialog(isOpen) {
                this.$nextTick(() => {
                    if (isOpen) {
                        this.$refs.newInquiryTitle?.focus();
                        return;
                    }
                    const button = this.$refs.newInquiryButton;
                    const el = button && (button.$el || button);
                    if (el && typeof el.focus === 'function') el.focus();
                });
            },
        },
    };
</script>

<style scoped>
    /* Alinhado ao dashboard de cursos (Courses.vue): largura máxima e tokens */
    .student-portal-inquiries {
        --dash-bg: #eef0f4;
        --dash-bg-elevated: #ffffff;
        --dash-text: #111827;
        --dash-text-muted: #6b7280;
        --dash-border: rgba(15, 23, 42, 0.08);
        min-height: 100%;
        background-color: #F5F7FB;
        text-align: left;
        box-sizing: border-box;
    }

    .student-portal-inquiries.dark {
        --dash-bg: #0f0e12;
        --dash-bg-elevated: #1a191f;
        --dash-text: #f3f4f6;
        --dash-text-muted: #9ca3af;
        --dash-border: rgba(255, 255, 255, 0.08);
        background-color: var(--fp-bg, #13121a);
    }

    .student-portal-inquiries__inner {
        max-width: 1920px;
        margin: 0 auto;
        padding: 18px 14px 32px;
        box-sizing: border-box;
    }

    .student-portal-inquiries__header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
        text-align: left;
        margin-bottom: 28px;
    }

    .student-portal-inquiries__header-main {
        min-width: 0;
    }

    .student-portal-inquiries__new-btn {
        flex-shrink: 0;
        margin-top: 4px;
    }

    .student-portal-inquiries__loading-text {
        color: var(--dash-text-muted);
    }

    .portal-inquiries__card--surface {
        background-color: var(--dash-bg-elevated) !important;
        color: var(--dash-text);
        border: 1px solid #e8eaf0;
        border-radius: 12px;
        box-shadow:
            0 1px 3px rgba(37, 8, 145, 0.06),
            0 6px 22px rgba(15, 23, 42, 0.06);
    }

    .portal-inquiries__card {
        padding: 14px !important;
    }

    .portal-inquiries__table-wrap {
        width: 100%;
        overflow-x: auto;
    }

    .inquiry-title-cell__date {
        margin-top: 4px;
        font-size: 0.78rem;
        color: var(--dash-text-muted);
        font-weight: 500;
    }

    :deep(.inquiries-table) {
        border: 1px solid #e8eaf0;
        border-radius: 10px;
        overflow: hidden;
    }

    :deep(.inquiries-table .v-data-table__th) {
        background: linear-gradient(180deg, #fafbff 0%, #ffffff 100%);
        color: #374151;
        font-weight: 600;
        border-bottom: 1px solid #eef0f4;
    }

    :deep(.inquiries-table .v-data-table__tr:hover) {
        background: rgba(37, 8, 145, 0.03);
    }

    :deep(.inquiries-table .v-data-table__td) {
        border-bottom: 1px solid #f1f3f7;
    }

    .portal-inquiries__filters {
        display: grid;
        grid-template-columns: minmax(260px, 1.6fr) minmax(160px, 0.8fr) minmax(
                180px,
                0.9fr
            ) auto;
        gap: 12px;
        align-items: center;
        border: 1px solid #e8eaf0;
        border-radius: 10px;
        padding: 10px;
        background: #fafbfc;
    }

    .portal-inquiries__filters-summary {
        font-size: 0.9rem;
        color: var(--dash-text-muted);
    }

    @media (max-width: 768px) {
        .student-portal-inquiries__inner {
            padding: 14px 10px 24px;
        }

        .student-portal-inquiries__header {
            margin-bottom: 14px;
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
        }

        .student-portal-inquiries__new-btn {
            align-self: stretch;
            margin-top: 0;
            min-height: 42px;
        }

        .portal-inquiries__card {
            padding: 10px !important;
        }

        .portal-inquiries__filters {
            grid-template-columns: 1fr;
            gap: 10px;
            padding: 8px;
        }

        .portal-inquiries__filters-summary {
            font-size: 0.82rem;
            margin-bottom: 8px !important;
        }

        :deep(.inquiries-table table) {
            min-width: 620px;
        }

        :deep(.inquiries-table .v-data-table__th),
        :deep(.inquiries-table .v-data-table__td) {
            padding: 10px 8px !important;
        }

        .inquiry-actions-cell {
            min-width: 88px;
            justify-content: flex-end !important;
        }

        .card-title {
            font-size: 24px;
            margin-bottom: 4px;
        }

        .card-subtitle {
            font-size: 14px;
        }
    }

    @media (max-width: 959px) {
        :deep(.inquiries-table th:nth-child(2)),
        :deep(.inquiries-table td:nth-child(2)) {
            display: none !important;
        }
    }

    .dark.student-portal-inquiries :deep(.inquiries-table) {
        background-color: var(--dash-bg-elevated);
        color: var(--dash-text);
    }

    .dark.student-portal-inquiries :deep(.inquiries-table .v-data-table__th) {
        background-color: #252730;
        color: var(--dash-text-muted);
        border-bottom-color: #3d414d;
    }

    .dark.student-portal-inquiries :deep(.inquiries-table .v-data-table__td) {
        color: var(--dash-text);
        border-bottom-color: #2d313c;
    }

    .dark.student-portal-inquiries :deep(.inquiries-table .v-data-table__tr:hover) {
        background: rgba(109, 90, 205, 0.12);
    }

    .dark.student-portal-inquiries :deep(.inquiries-table) {
        border-color: #3d414d;
    }

    .dark.student-portal-inquiries :deep(.v-data-table-footer) {
        background-color: var(--dash-bg-elevated);
        color: var(--dash-text-muted);
        border-color: var(--dash-border);
    }

    .dark.student-portal-inquiries .portal-inquiries__card--surface {
        border-color: #353945;
        box-shadow:
            0 1px 4px rgba(0, 0, 0, 0.35),
            0 8px 22px rgba(0, 0, 0, 0.24);
    }

    .dark.student-portal-inquiries .portal-inquiries__filters {
        background: #1e2028;
        border-color: #3d414d;
    }

    .dark.student-portal-inquiries :deep(.v-divider) {
        border-color: var(--dash-border);
    }

    .inquiry-details {
        background-color: #f5f5f5;
        border-radius: 8px;
        padding: 16px;
    }

    .dark .inquiry-details {
        background-color: #1e1e1e;
    }

    .description-inquiry {
        color: #333;
    }

    .dark .description-inquiry {
        color: #e0e0e0;
    }

    /* Modal detalhe da dúvida (padrão portal; ok com teleport do v-dialog) */
    .portal-inquiry-detail-card {
        --pid-bg: #ffffff;
        --pid-text: #111827;
        --pid-muted: #6b7280;
        --pid-border: rgba(15, 23, 42, 0.1);
        --pid-elevated: #f3f4f6;
        --pid-chip-bg: rgba(26, 196, 205, 0.12);
        background: var(--pid-bg) !important;
        color: var(--pid-text);
        border: 1px solid var(--pid-border);
        border-radius: 14px;
        box-shadow:
            0 10px 40px rgba(15, 23, 42, 0.12),
            0 1px 3px rgba(15, 23, 42, 0.06);
        overflow: hidden;
    }

    .portal-inquiry-detail-card--dark {
        --pid-bg: #1a191f;
        --pid-text: #f3f4f6;
        --pid-muted: #9ca3af;
        --pid-border: rgba(255, 255, 255, 0.1);
        --pid-elevated: #252730;
        --pid-chip-bg: rgba(26, 196, 205, 0.2);
        box-shadow:
            0 16px 48px rgba(0, 0, 0, 0.45),
            0 1px 4px rgba(0, 0, 0, 0.35);
    }

    .portal-inquiry-detail-card__head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
        padding: 20px 20px 16px !important;
        white-space: normal;
    }

    .portal-inquiry-detail-card__head-main {
        min-width: 0;
        flex: 1 1 auto;
        text-align: left;
    }

    .portal-inquiry-detail-card__eyebrow {
        font-size: 0.7rem;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        font-weight: 700;
        color: var(--pid-muted);
        margin: 0 0 6px;
    }

    .portal-inquiry-detail-card__title {
        font-size: 1.2rem;
        font-weight: 700;
        line-height: 1.35;
        margin: 0 0 10px;
        color: var(--pid-text);
    }

    .portal-inquiry-detail-card__meta {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px;
    }

    .portal-inquiry-detail-card__date {
        font-size: 0.82rem;
        color: var(--pid-muted);
    }

    .portal-inquiry-detail-card__close {
        flex-shrink: 0;
        margin-top: -2px;
        color: var(--pid-muted) !important;
    }

    .portal-inquiry-detail-card__divider {
        border-color: var(--pid-border) !important;
        opacity: 1;
    }

    .portal-inquiry-detail-card__body {
        padding: 20px !important;
        background: var(--pid-bg);
    }

    .portal-inquiry-detail-card__section {
        margin-bottom: 22px;
    }

    .portal-inquiry-detail-card__section--composer {
        margin-bottom: 0;
        margin-top: 4px;
        padding-top: 18px;
        border-top: 1px dashed var(--pid-border);
    }

    .portal-inquiry-detail-card__section-title {
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        font-weight: 700;
        color: var(--pid-muted);
        margin: 0 0 10px;
        text-align: left;
    }

    .portal-inquiry-detail-card__message {
        padding: 14px 16px;
        border-radius: 10px;
        background: var(--pid-elevated);
        border: 1px solid var(--pid-border);
        font-size: 0.94rem;
        line-height: 1.55;
        color: var(--pid-text);
        text-align: left;
        word-break: break-word;
    }

    .portal-inquiry-detail-card__message.description-inquiry :deep(img) {
        max-width: 100%;
        height: auto;
        border-radius: 6px;
    }

    .portal-inquiry-detail-card__replies :deep(.reply-item) {
        background: var(--pid-elevated);
        border: 1px solid var(--pid-border);
        border-radius: 10px;
        margin: 0 0 12px;
        padding: 14px 16px;
    }

    .portal-inquiry-detail-card__replies :deep(.reply-item:last-of-type) {
        margin-bottom: 0;
    }

    .portal-inquiry-detail-card__replies :deep(.content_reply) {
        font-size: 0.92rem;
        line-height: 1.5;
        color: var(--pid-text);
        margin-bottom: 12px;
        text-align: left;
    }

    .portal-inquiry-detail-card__replies :deep(.autor) {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 12px;
    }

    .portal-inquiry-detail-card__replies :deep(.autor .logo) {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: var(--pid-chip-bg);
        color: #0f766e;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 0.72rem;
        font-weight: 700;
    }

    .portal-inquiry-detail-card--dark
        .portal-inquiry-detail-card__replies
        :deep(.autor .logo) {
        color: #5eead4;
    }

    .portal-inquiry-detail-card__replies :deep(.autor .name) {
        font-weight: 600;
        font-size: 0.9rem;
        color: var(--pid-text);
    }

    .portal-inquiry-detail-card__replies :deep(.autor .date) {
        font-size: 0.75rem;
        color: var(--pid-muted);
    }

    .portal-inquiry-detail-card__replies :deep(.empty-state) {
        border: 1px dashed var(--pid-border);
        border-radius: 10px;
        padding: 20px 16px;
        color: var(--pid-muted);
        font-style: normal;
        font-size: 0.9rem;
        background: var(--pid-elevated);
        text-align: center;
    }

    .portal-inquiry-detail-card__alert-wrap {
        margin-bottom: 18px;
    }

    .portal-inquiry-detail-card__alert {
        border-radius: 10px;
    }

    .portal-inquiry-detail-card__submit {
        font-weight: 600;
    }

    @media (max-width: 600px) {
        .portal-inquiry-detail-card__head {
            padding: 16px 14px 12px !important;
        }

        .portal-inquiry-detail-card__title {
            font-size: 1.05rem;
        }

        .portal-inquiry-detail-card__body {
            padding: 16px 14px 18px !important;
        }
    }

    .gap-2 {
        gap: 8px;
    }

    .card-title {
        font-size: 28px;
        font-weight: 700;
        color: var(--dash-text);
        margin-bottom: 8px;
    }

    .card-subtitle {
        font-size: 16px;
        color: var(--dash-text-muted);
        margin-bottom: 0;
    }
</style>

<style src="./css/forum-portal.css"></style>
<style src="./css/inquiry-portal.css"></style>
